import { FCHI2_RECORD_TYPE } from 'app/services/sfAuth/sfData/sfOpportunity'
import { reportIsEditable, sanitizeReportType } from 'app/services/sfAuth/sfData/sfReports'
import { getLabelFromTranslationData } from 'app/views/common/translationsCommon'
import { matchOpportunityWithFundingStream } from 'app/views/internal/funding-streams/ConfigureFundingStreams'
import moment from 'moment'
import { myI18n } from 'translation/I18nConnectedProvider'

/**
 * Maps Salesforce opportunity records to a format suitable for a data table display of grantee reports.
 * Filters opportunities based on edit access, valid report types, and conditions specific to internal vs non-internal users.
 * @function
 * @param {Object} params - The parameters for mapping reports.
 * @param {Array} params.opportunities - List of opportunity objects from Salesforce.
 * @param {Array} params.fundingStreams - List of funding stream objects.
 * @param {boolean} params.isInternal - Whether the current user is internal or not.
 * @param {Object} params.user - The current user object containing user data like language preferences.
 * @param {Object} params.organization - The organization object used to filter reports by account ID.
 * @returns {Array} A filtered array of reports formatted for the data table display.
 */
export const mapReportsForDataTable = ({
  opportunities,
  fundingStreams,
  isInternal,
  user,
  onlyLast30days = false,
  organization
}) => {
  let reports = []
  const opportunitiesIds = []

  opportunities = opportunities.filter(function (opp) {
    return opp.UserRecordAccess.HasEditAccess
  })
  opportunities
    .filter(
      (opp) =>
        opp.FGM_Base__Grantee_Reports__r &&
        opp.RecordType.Name !== FCHI2_RECORD_TYPE
    )
    .forEach((opp) => {
      opp.FGM_Base__Grantee_Reports__r.records.filter(report => {
        if (onlyLast30days) {
          return report.Last_Modified_By_User_Date__c && moment.utc().diff(moment.utc(report.Last_Modified_By_User_Date__c), 'days') <= 30
        } else {
          return true
        }
      }).forEach((report) => {
        if (
          !report.Type__c ||
          report.Type__c === 'Narrative and Financial'
        ) {
          console.warn('Invalid record type', report)
        }
        if (!opportunitiesIds.includes(opp.Id)) {
          opportunitiesIds.push(opp.Id)
        }
        const toSet = {}
        toSet.lastModifiedUserData = report.Last_Modified_User_Data__c
        toSet.lastModifiedDate = report.Last_Modified_By_User_Date__c
        toSet.dataTableItemType = 'granteeReport'
        toSet.opportunityId = opp.Id
        toSet.fundingStreamId = opp.Funding_Stream__c
        toSet.opportunity = opp.Name
        toSet.name = myI18n._(report.Type__c)
        toSet.survey = report.Survey__c
        toSet.accountId = opp.AccountId
        toSet.reportType = sanitizeReportType(report.Type__c)
        toSet.status = report.FGM_Base__Status__c
        toSet.id = report.Id
        toSet.due = {
          isOverdue: report.FGM_Base__Is_Overdue__c,
          dueDate: report.FGM_Base__Due_Date__c
        }
        toSet.actions = {
          id: report.Id,
          isInvalid:
            !report.Type__c ||
            report.Type__c === 'Narrative and Financial',
          status: report.FGM_Base__Status__c,
          isEditable: reportIsEditable(report.FGM_Base__Status__c),
          submittedManually: report.Submitted_Manually__c
        }
        toSet.type = report.attributes.type
        reports.push(toSet)
      })
    })

  reports = reports.filter((report) => report.status !== 'Obsolete')
  if (!isInternal) {
    reports = reports.filter(
      (report) => report.reportType !== 'Phone call'
    )
  }
  reports.forEach((report) => {
    const config = fundingStreams.find(
      (fs) => fs.id === report.fundingStreamId
    )

    const fundingStream = matchOpportunityWithFundingStream({
      fundingStreams,
      fundingStreamId: report.fundingStreamId
    })
    if (config) {
      report.actions.surveyFormId = config.granteeReportSurveyForm
    }
    if (fundingStream) {
      report.fundingStream = getLabelFromTranslationData({
        data: fundingStream.fullName,
        langVersion: user.language
      })
    }
  })
  return reports.filter((report) => report.accountId === organization.id)
}
